// @noflow

import { values } from 'lodash';

import deviceType from 'Common/app/getDeviceType';
import { getJson, delJson, put, post, postJson } from '../utilities/fetchUtils';
import { setRequireRefresh } from '../franchise/actions';
import { storeLegacyDeviceList } from '../preferences/actions';
import { profileNotificationsEmitter } from './ProfileNotifications';
import { findPrimaryProfile } from './utils';

export const setProfileModalRequired = required => ({ dispatch }) => {
  dispatch({
    type: 'SET_PROFILE_MODAL_REQUIRED',
    payload: required,
  });
};

const setDefaultProfileIdIfNecessary = () => ({ getState, dispatch }) => {
  const state = getState();
  const { profiles } = state.profiles.content;
  const currentProfileId = state.profiles.currentProfile.profileId;

  /* If there is only 1 profile and rs cookie (which embeds profile_id) is not set - set this profile as "active" by default */
  if (currentProfileId === undefined) {
    if (profiles.length === 1 || (deviceType === 'Tv' && profiles.length > 0)) {
      const defaultProfile = findPrimaryProfile(profiles);
      const defaultProfileId = defaultProfile?.id;

      dispatch({ type: 'SET_DEFAULT_PROFILE_ID', payload: { profileId: defaultProfileId } });

      /* eslint-disable-next-line no-use-before-define */
      dispatch(setCurrentProfileId(defaultProfileId)); // set existing profile id as default
      dispatch(setProfileModalRequired(false));
    } else {
      /* eslint-disable-next-line */
      dispatch(fetchActiveProfile());
    }
  }
};

export const fetchProfiles = () => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;
  if (!euid) {
    return undefined;
  }

  dispatch({ type: 'REQUEST_USER_PROFILES' });

  const url = `${config.session.radish_url}profiles`;

  return getJson({ url, state })
    .then(payload => {
      dispatch({ type: 'RECEIVE_USER_PROFILES', payload });
      dispatch(setDefaultProfileIdIfNecessary());
    })
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'REQUEST_USER_PROFILES_ERROR' });
    });
};

export const deleteProfile = (profileId, onDeleteProfileCompleted) => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  dispatch({
    type: 'REQUEST_DELETE_USER_PROFILE',
    profileId,
  });

  const url = `${config.session.radish_url}profiles/${profileId}`;
  return delJson({ url, state })
    .then(() => {
      dispatch({
        type: 'RECEIVE_DELETE_USER_PROFILE',
        profileId,
      });
      onDeleteProfileCompleted();
    })
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'REQUEST_DELETE_USER_PROFILE_ERROR', profileId });
      onDeleteProfileCompleted();
    });
};

export const createProfile = (profile, callback) => ({ getState, dispatch, config }) => {
  const state = getState();
  const { name, role, avatar_link, age_level, theme } = profile; /* eslint-disable-line camelcase */

  dispatch({
    type: 'CREATE_USER_PROFILE',
    profile,
  });

  const url = `${config.session.radish_url}profiles`;
  const data = { name, role, avatar_link, age_level, theme };
  return post({ url, state, data })
    .then(() => {
      dispatch({ type: 'CREATE_USER_PROFILE_SUCCESS', profile });
      if (callback) {
        callback(true);
      }
    })
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'CREATE_USER_PROFILE_ERROR', profile });
      if (callback) {
        callback(false);
      }
    });
};

export const fetchProfileImages = () => ({ getState, dispatch, config }) => {
  const state = getState();

  dispatch({
    type: 'REQUEST_USER_PROFILE_IMAGES',
  });

  const url = `${config.session.radish_url}/profile_images`;

  return getJson({ url, state })
    .then(payload => {
      dispatch({ type: 'RECEIVE_USER_PROFILE_IMAGES', payload });
    })
    .catch((error: Error) => {
      console.warn('error during fetching avatar list', error); /* eslint-disable-line no-console */
      dispatch({ type: 'REQUEST_USER_PROFILE_IMAGES_ERROR' });
    });
};

export const updateProfile = (profile, callback) => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    id,
    name,
    role,
    avatar_link /* eslint-disable-line camelcase */,
    age_level /* eslint-disable-line camelcase */,
    theme,
  } = profile;

  dispatch({
    type: 'UPDATE_USER_PROFILE',
    profile,
  });

  const url = `${config.session.radish_url}/profiles/${id}`;
  const data = { name, role, avatar_link, age_level, theme };
  return put({ url, state, data })
    .then(() => {
      dispatch({ type: 'UPDATE_USER_PROFILE_SUCCESS', profile });
      if (callback) {
        callback(true);
      }
      if (id === state.profiles.currentProfile.profileId) {
        dispatch({
          type: 'RECEIVE_SET_CURRENT_PROFILE_ID',
          payload: { id, role, age_level },
        });
      }
    })
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'UPDATE_USER_PROFILE_ERROR', profile });
      if (callback) {
        callback(false);
      }
    });
};

export const checkPasscodeExists = () => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return undefined;
  }
  dispatch({
    type: 'REQUEST_CHECK_PASSCODE_EXIST',
  });

  if (!euid) {
    return Promise.resolve();
  }

  const url = `${config.session.radish_url}/v20/dol/users/show/${euid}`;
  return getJson({ url, state })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_CHECK_PASSCODE_EXIST',
        payload: payload.parental_controls.passcode,
      });
      dispatch(storeLegacyDeviceList(payload.devices));
    })
    .catch(error => {
      console.warn('fetch error', error);
      dispatch({ type: 'REQUEST_CHECK_PASSCODE_EXIST_ERROR' });
    });
};

export const checkSecurityQuestionExists = () => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return Promise.resolve();
  }
  dispatch({
    type: 'REQUEST_CHECK_SECURITY_QUESTION_EXIST',
  });

  const url = `${config.session.radish_url}/v20/dol/users/show/${euid}`;
  return getJson({ url, state })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_CHECK_SECURITY_QUESTION_EXIST',
        payload: payload.parental_controls_security_question.security_question,
      });
    })
    .catch(error => {
      console.warn('fetch error', error);
      dispatch({ type: 'REQUEST_CHECK_SECURITY_QUESTION_EXIST_ERROR' });
    });
};

export const changeMode = mode => ({
  type: 'PROFILE_MODE_CHANGE',
  payload: mode,
});

export const setProfileForUpadateOnWebForm = profile => ({
  type: 'SET_PROFILE_FOR_UPADATE_ON_WEB',
  payload: profile,
});

export const fetchParentalControlRatings = () => ({ getState, dispatch, config }) => {
  const state = getState();
  if (!state.auth.viewer) return undefined;

  dispatch({
    type: 'REQUEST_PARENTAL_CONTROL_RATINGS',
  });

  const urlAvailableRatings = `${config.session.radish_url}v20/dol/parental_controls/settings.json`;
  const urlInstalledRatings = `${config.session.radish_url}v20/dol/users/show.json`;

  return Promise.all([
    getJson({ url: urlAvailableRatings, state }),
    getJson({ url: urlInstalledRatings, state }),
  ])
    .then(
      ([availableSettings, installedSettings]: [
        ParentalControlAvailableRatings,
        ParentalControlInstalledSettings,
      ]) => {
        dispatch({
          type: 'RECEIVE_PARENTAL_CONTROL_RATINGS',
          payload: { availableSettings, installedSettings },
        });
      },
    )
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'REQUEST_PARENTAL_CONTROL_RATINGS_ERROR' });
    });
};

function getCurrentRatingSlug(ratingList) {
  const rating = ratingList.find(element => element.isEnabled);
  return rating ? rating.slug : '';
}

export const applyParentalControlRatings = () => ({ getState, dispatch, config }) => {
  const state = getState();

  const showRating = getCurrentRatingSlug(state.profiles.parentalControlRatings.showRatings);
  const movieRating = getCurrentRatingSlug(state.profiles.parentalControlRatings.movieRatings);
  const headers = { 'Content-Type': 'application/json' };
  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  dispatch({
    type: 'REQUEST_APPLY_PARENTAL_CONTROL_RATINGS',
  });

  const url = `${config.session.radish_url}v20/dol/parental_controls/set.json`;

  const data = JSON.stringify({
    uuid,
    settings: {
      passcode: state.profiles.parentalControlRatings.passcode,
      movie_ratings_to_block: movieRating,
      show_ratings_to_block: showRating,
    },
  });

  return postJson({ url, state, data, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_APPLY_PARENTAL_CONTROL_RATINGS' });
      dispatch(setRequireRefresh(true));
      profileNotificationsEmitter.next({ type: 'parentalControlsSettingsSaved' });
    })
    .catch(error => {
      console.warn('fetch error', error); /* eslint-disable-line no-console */
      dispatch({ type: 'REQUEST_APPLY_PARENTAL_CONTROL_RATINGS_ERROR' });
      profileNotificationsEmitter.next({ type: 'parentalControlsSettingsSaveError' });
    });
};

export const updateParentalControlRatings = (
  movieRatings: Array<Rating>,
  showRatings: Array<Rating>,
) => ({ dispatch }) => {
  dispatch({
    type: 'UPDATE_PARENTAL_CONTROL_RATINGS',
    payload: { movieRatings, showRatings },
  });
  dispatch(applyParentalControlRatings());
};

export const setParentalControlRatings = (newRating, type, shouldApplyRatings = true) => ({
  dispatch,
}) => {
  dispatch({
    type: 'SET_PARENTAL_CONTROL_RATINGS',
    payload: { type, rating: newRating },
  });

  if (shouldApplyRatings) {
    dispatch(applyParentalControlRatings());
  }
};

export const setCurrentProfileId = (newProfileId, role, ageLevel) => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const {
    auth: { viewer: { euid } = {} },
  } = state;

  if (!euid) {
    return;
  }

  dispatch({ type: 'REQUEST_SET_CURRENT_PROFILE_ID' });

  const url = `${config.session.radish_url}v20/dol/users/${euid}/profiles/${newProfileId}/switch`;
  const data = { euid, profile_id: newProfileId };

  return put({ url, state, data })
    .then(() => {
      dispatch({
        type: 'RECEIVE_SET_CURRENT_PROFILE_ID',
        payload: { newProfileId, role, ageLevel },
      });
    })
    .catch(error => {
      console.warn('REQUEST_SET_CURRENT_PROFILE_ID_ERROR', 'profile was not switched:', error);
      dispatch({ type: 'REQUEST_SET_CURRENT_PROFILE_ID_ERROR' });
    });
};

export const setRequireParentalControlPasscode = require => ({ getState, dispatch, config }) => {
  const state = getState();

  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  if (!uuid) {
    return undefined;
  }

  dispatch({
    type: 'REQUEST_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE',
    payload: { require },
  });

  const headers = { 'Content-Type': 'application/json' };
  const url = `${
    config.session.radish_url
  }/v20/dol/users/${uuid}/profiles/ignored_profile_id/require_passcode`;

  const data = JSON.stringify({ require_passcode: require });

  return put({ state, url, data, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE' });
    })
    .catch(error => {
      console.warn(`Error during setting parental control passcode preferences: ${error} `);
      dispatch({ type: 'REQUEST_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE_ERROR' });
    });
};

export const fetchActiveProfile = () => ({ dispatch, config, getState }) => {
  if (process.env.IN_CHROMECAST_MODE) return undefined;

  const state = getState();

  dispatch({ type: 'REQUEST_ACTIVE_PROFILE' });

  const url = `${config.session.radish_url}active_profile`;

  return getJson({ state, url })
    .then(profileHashMap => {
      if (profileHashMap) {
        dispatch({
          type: 'RECEIVE_ACTIVE_PROFILE',
          payload: { profile: profileHashMap[Object.keys(profileHashMap)[0]] },
        });
      } else {
        /* Show switch profile selector */
        dispatch(setProfileModalRequired(true));
      }
    })
    .catch(err => {
      dispatch({ type: 'REQUEST_ACTIVE_PROFILE_ERROR' });
      console.warn('REQUEST_ACTIVE_PROFILE_ERROR: ', err.message);
    });
};
